let audioContext = null;
let permissionRequested = false;

const hasNotifications = () => typeof window !== "undefined" && "Notification" in window;

const getAudioContext = () => {
  if (audioContext) return audioContext;
  if (typeof window === "undefined") return null;
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtor) return null;
  try {
    audioContext = new AudioCtor();
  } catch (e) {
    // Some browsers refuse to construct a context before a user gesture.
    return null;
  }
  return audioContext;
};

const TONE_URL = `${process.env.PUBLIC_URL || ""}/foodoor-order-alert.mp3`;

let toneBuffer = null;
let tonePending = null;

const loadTone = (context) => {
  if (toneBuffer) return Promise.resolve(toneBuffer);
  if (!tonePending) {
    tonePending = fetch(TONE_URL)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.arrayBuffer();
      })
      .then((bytes) => context.decodeAudioData(bytes))
      .then((buffer) => {
        toneBuffer = buffer;
        return buffer;
      })
      .catch(() => {
        tonePending = null;
        return null;
      });
  }
  return tonePending;
};

export const alertsReady = () => ({
  sound: Boolean(audioContext) && audioContext.state === "running",
  notifications: hasNotifications() && Notification.permission === "granted",
  notificationsBlocked: hasNotifications() && Notification.permission === "denied",
  supported: {
    sound: typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext),
    notifications: hasNotifications(),
  },
});

export const unlockSound = async () => {
  const context = getAudioContext();
  if (!context) return false;
  if (context.state === "suspended") {
    try {
      await context.resume();
    } catch (e) {
      // Still blocked; the next user gesture gets another chance.
      return false;
    }
  }
  if (context.state === "running") loadTone(context);
  return context.state === "running";
};

export const requestNotificationPermission = async () => {
  if (!hasNotifications()) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  if (permissionRequested) return Notification.permission === "granted";
  permissionRequested = true;
  try {
    const result = await Notification.requestPermission();
    return result === "granted";
  } catch (e) {
    return false;
  }
};

export const enableAlerts = async () => {
  await unlockSound();
  await requestNotificationPermission();
  return alertsReady();
};

// Browsers only allow audio once the page has had a user gesture, so the first
// tap anywhere arms the tone even if the rider never presses "Enable alerts".
export const armSoundOnFirstGesture = () => {
  if (typeof window === "undefined") return () => {};
  const handler = () => {
    unlockSound();
  };
  window.addEventListener("pointerdown", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
  return () => {
    window.removeEventListener("pointerdown", handler);
    window.removeEventListener("keydown", handler);
  };
};

// Fallback when the tone cannot be fetched or decoded: two rising beeps that
// stay audible from a bike mount.
const playFallbackBeeps = (context) => {
  const startedAt = context.currentTime;
  [
    [880, 0],
    [1174.66, 0.18],
  ].forEach(([frequency, offset]) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const at = startedAt + offset;

    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(0.35, at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + 0.18);
  });
};

export const playAlertTone = () => {
  const context = getAudioContext();
  if (!context || context.state !== "running") return false;

  loadTone(context).then((buffer) => {
    if (!buffer) {
      playFallbackBeeps(context);
      return;
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.start();
  });

  return true;
};

export const showOrderNotification = (body) => {
  if (!hasNotifications() || Notification.permission !== "granted") return false;
  try {
    const notification = new Notification("Foodoor Delivery", {
      body,
      tag: "foodoor-new-orders",
      renotify: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    return true;
  } catch (e) {
    return false;
  }
};
